import React from 'react';
import { CheckCircle2, Loader2, PencilLine, Search, Trash2 } from 'lucide-react';
import { FRIST_KATEGORIEN, FristKategorie, SifaFrist } from '../../services/sifa/betreuungTypes';
import { formatDatum } from './unterweisungModulLogic';
import { AMPEL_FRIST, AmpelFn, FristenFilter, filtereFristen, kategorieLabel, sortiereNachAmpel, wiederholungText } from './fristenModulLogic';

interface Props {
  fristen: SifaFrist[];
  laden: boolean;
  filter: FristenFilter;
  onFilter: (f: FristenFilter) => void;
  ampel: AmpelFn;
  /** Anzeigename je Mitarbeiter-Id (fuer die Spalte „Person"). */
  personName: (id: string | null) => string | null;
  isReadOnly?: boolean;
  isMobile?: boolean;
  onBearbeiten: (f: SifaFrist) => void;
  onErledigen: (f: SifaFrist) => void;
  onLoeschen: (f: SifaFrist) => void;
}

/**
 * Das Fristenregister als Tabelle (Handy: Karten) mit Suche, Kategorie-
 * Filter und „Erledigte anzeigen". Offene Zeilen stehen nach Ampel sortiert
 * (rot zuerst), erledigte — wenn eingeblendet — dahinter, juengste zuerst.
 */
export const FristenListe: React.FC<Props> = ({
  fristen, laden, filter, onFilter, ampel, personName, isReadOnly = false, isMobile = false, onBearbeiten, onErledigen, onLoeschen,
}) => {
  const gefiltert = filtereFristen(fristen, filter);
  const offen = sortiereNachAmpel(gefiltert.filter((f) => !f.erledigtAm), ampel);
  const erledigt = gefiltert.filter((f) => !!f.erledigtAm).sort((a, b) => (b.erledigtAm || '').localeCompare(a.erledigtAm || ''));
  const sichtbar = [...offen, ...erledigt];

  const ampelBadge = (f: SifaFrist) => {
    if (f.erledigtAm) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-bold border rounded-full text-slate-500 bg-slate-50 border-slate-200">
          <CheckCircle2 className="w-3 h-3" /> erledigt {formatDatum(f.erledigtAm)}
        </span>
      );
    }
    const a = AMPEL_FRIST[ampel(f)];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-bold border rounded-full ${a.klasse}`}>
        <span className={`w-2 h-2 rounded-full ${a.punkt}`} />{a.label}
      </span>
    );
  };

  const wer = (f: SifaFrist) => [personName(f.mitarbeiterId), f.zustaendig].filter(Boolean).join(' · ');

  const aktionen = (f: SifaFrist) => (
    <div className="flex items-center gap-1">
      {!isReadOnly && !f.erledigtAm && (
        <button onClick={() => onErledigen(f)} className="min-h-[36px] px-2.5 flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg" title="Als erledigt eintragen">
          <CheckCircle2 className="w-3.5 h-3.5" /> Erledigt
        </button>
      )}
      <button onClick={() => onBearbeiten(f)} className="min-h-[36px] px-2.5 flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg">
        <PencilLine className="w-3.5 h-3.5" /> {isReadOnly ? 'Ansehen' : 'Bearbeiten'}
      </button>
      {!isReadOnly && (
        <button onClick={() => onLoeschen(f)} className="min-h-[36px] min-w-[36px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg" title="Löschen">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={filter.suche} onChange={(e) => onFilter({ ...filter, suche: e.target.value })} placeholder="Titel, Zuständiger, Notiz…" className="w-full pl-9 pr-3 py-2 min-h-[40px] text-sm bg-white border border-slate-200 rounded-xl outline-none focus:ring-1 focus:ring-amber-300" />
        </div>
        <select value={filter.kategorie} onChange={(e) => onFilter({ ...filter, kategorie: e.target.value as FristKategorie | 'alle' })} className="min-h-[40px] px-3 text-sm bg-white border border-slate-200 rounded-xl outline-none">
          <option value="alle">Alle Kategorien</option>
          {FRIST_KATEGORIEN.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={filter.erledigteAnzeigen} onChange={(e) => onFilter({ ...filter, erledigteAnzeigen: e.target.checked })} className="w-4 h-4 accent-amber-600" />
          Erledigte anzeigen
        </label>
        <span className="text-xs text-slate-500">{sichtbar.length} von {fristen.length}</span>
      </div>

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : sichtbar.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">{fristen.length === 0 ? 'Noch keine Frist eingetragen' : 'Kein Treffer'}</p>
          {fristen.length === 0 && (
            <p className="text-xs text-slate-500 mt-1">Prüffristen, Vorsorge, Unterweisungen, ASA-Sitzungen — jede Frist mit Wiederholung legt beim Erledigen die nächste an.</p>
          )}
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sichtbar.map((f) => (
            <div key={f.id} className={`bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5 ${f.erledigtAm ? 'opacity-70' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{f.titel}</p>
                  <p className="text-xs text-slate-500 truncate">{kategorieLabel(f.kategorie)} · {wiederholungText(f.intervallMonate)}{wer(f) ? ` · ${wer(f)}` : ''}</p>
                </div>
                <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">{formatDatum(f.faelligAm)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {ampelBadge(f)}
                {aktionen(f)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Status</th>
                <th className="px-3 py-2 text-left font-bold">Fällig am</th>
                <th className="px-3 py-2 text-left font-bold">Kategorie</th>
                <th className="px-3 py-2 text-left font-bold">Titel</th>
                <th className="px-3 py-2 text-left font-bold">Person / Zuständig</th>
                <th className="px-3 py-2 text-left font-bold">Wiederholung</th>
                <th className="px-3 py-2 text-left font-bold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((f) => (
                <tr key={f.id} className={`hover:bg-slate-50/60 ${f.erledigtAm ? 'text-slate-400' : ''}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{ampelBadge(f)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-semibold text-slate-700">{formatDatum(f.faelligAm)}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{kategorieLabel(f.kategorie)}</td>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{f.titel}</p>
                    {f.notiz && <p className="text-xs text-slate-500 truncate max-w-[320px]" title={f.notiz}>{f.notiz}</p>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{wer(f) || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{wiederholungText(f.intervallMonate)}</td>
                  <td className="px-3 py-2">{aktionen(f)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
